import { apiClient } from "@api/client";
import { request, requestVoid } from "@api/request";
import type {
  EmailSetupIn,
  EmailSetupOut,
  EmailDraftContextOut,
  EmailDraftOut,
  EmailLiveMessage,
  EmailAssistantIn,
  EmailDraftRevisionIn,
} from "@api/types";
import { ClinicAuthError } from "@auth/clinicAuthClient";

export interface EmailSetup {
  mailbox: string | null;
  connected: boolean;
  configured: boolean;
  lastSyncAt: string | null;
  prerequisites: string[];
}

interface EmailAttachment {
  filename: string;
  contentType: string;
  text: string;
  parseStatus: string;
}

export interface EmailMessage {
  uuid: string;
  providerId: string;
  threadId: string;
  sender: string;
  subject: string;
  createdAt: string;
  availability: string;
  association: EmailLiveMessage["association"];
  body: string;
  attachments: EmailAttachment[];
}

type EmailDraftStatus = EmailDraftOut["status"];

export interface EmailDraft {
  deliveryError: string | null;
  cc: string[];
  uuid: string;
  sender: string;
  recipient: string;
  subject: string;
  body: string;
  revision: number;
  status: EmailDraftStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  providerId: string | null;
  sourceMessageUuid: string | null;
  threadId: string | null;
  createdAt: string;
}

export interface EmailConversationMessage {
  providerId: string;
  sender: string;
  subject: string;
  body: string;
  createdAt: string | null;
  mine: boolean;
  sentByBrian: boolean;
}

export interface EmailDraftContext {
  messages: EmailConversationMessage[];
  drafts: EmailDraft[];
  totalMessages: number;
  conversationKey: string | null;
  nextBefore: number | null;
}

export type EmailSetupInput = EmailSetupIn;

export type EmailDraftRevision = EmailDraftRevisionIn;

/** Callers read it to detect the last page. */
const EMAIL_PAGE_SIZE = 20;

const TERMINAL_DRAFT_STATUSES: ReadonlySet<EmailDraftStatus> = new Set([
  "sent",
  "discarded",
]);

/** The backend answers 404 on every email route when the feature is off. */
export const isEmailUnavailable = (error: unknown): boolean =>
  error instanceof ClinicAuthError && error.status === 404;

export const isTerminalDraft = (status?: EmailDraftStatus): boolean =>
  !!status && TERMINAL_DRAFT_STATUSES.has(status);

export const isEditableDraft = (status: EmailDraftStatus): boolean =>
  status === "draft" || status === "failed";

const toSetup = (setup: EmailSetupOut): EmailSetup => ({
  mailbox: setup.mailbox,
  connected: setup.connected,
  configured: setup.configured,
  lastSyncAt: setup.last_sync_at,
  prerequisites: setup.prerequisites,
});

const toMessage = (message: EmailLiveMessage): EmailMessage => ({
  association: message.association,
  uuid: message.uuid,
  providerId: message.provider_id,
  threadId: message.thread_id,
  sender: message.sender,
  subject: message.subject,
  availability: message.availability,
  body: message.body,
  attachments: (message.attachments ?? []).map((attachment) => ({
    filename: String(attachment.filename ?? ""),
    contentType: String(attachment.content_type ?? ""),
    text: String(attachment.text ?? ""),
    parseStatus: String(attachment.parse_status ?? ""),
  })),
  createdAt: message.created_at,
});

const toDraft = (draft: EmailDraftOut): EmailDraft => ({
  deliveryError: draft.delivery_error ?? null,
  cc: draft.cc ?? [],
  uuid: draft.uuid,
  sender: draft.sender,
  recipient: draft.recipient,
  subject: draft.subject,
  body: draft.body,
  revision: draft.revision,
  status: draft.status,
  approvedBy: draft.approved_by,
  approvedAt: draft.approved_at,
  providerId: draft.provider_id,
  sourceMessageUuid: draft.source_message_uuid,
  threadId: draft.thread_id ?? null,
  createdAt: draft.created_at,
});

const toDraftContext = (context: EmailDraftContextOut): EmailDraftContext => ({
  drafts: (context.drafts ?? []).map(toDraft),
  conversationKey: context.conversation_key ?? null,
  totalMessages: context.total_messages ?? context.messages.length,
  nextBefore: context.next_before ?? null,
  messages: context.messages.map((message) => ({
    providerId: message.provider_id,
    sender: message.sender,
    subject: message.subject,
    body: message.body,
    createdAt: message.created_at,
    mine: message.mine,
    sentByBrian: message.sent_by_brian,
  })),
});

export const emailClient = {
  listConversations: (inbox: boolean, offset = 0) =>
    request(() =>
      apiClient.GET("/email/conversations", {
        params: { query: { inbox, offset, limit: EMAIL_PAGE_SIZE } },
      }),
    ),
  getConversation: (kind: "draft" | "message", uuid: string, before = 0) =>
    (kind === "draft"
      ? request(() =>
          apiClient.GET("/email/drafts/{draft_uuid}/context", {
            params: { path: { draft_uuid: uuid }, query: { before } },
          }),
        )
      : request(() =>
          apiClient.GET("/email/messages/{message_uuid}/context", {
            params: { path: { message_uuid: uuid }, query: { before } },
          }),
        )
    ).then(toDraftContext),
  getSetup: () => request(() => apiClient.GET("/email/setup")).then(toSetup),
  updateSetup: (body: EmailSetupInput) =>
    request(() => apiClient.PUT("/email/setup", { body })).then(toSetup),
  startOauth: async () => {
    // The response sets the state cookie the callback verifies; cross-origin
    // in local dev, so the browser only stores it with credentials included.
    const result = await request(() =>
      apiClient.POST("/email/oauth/start", { credentials: "include" }),
    );
    if (typeof result.url !== "string")
      throw new Error("Google authorization URL is unavailable");
    return { url: result.url };
  },
  disconnect: () => requestVoid(() => apiClient.POST("/email/disconnect")),
  reconcileDraft: (uuid: string) =>
    requestVoid(() =>
      apiClient.POST("/email/drafts/{draft_uuid}/reconcile", {
        params: { path: { draft_uuid: uuid } },
      }),
    ),
  linkMessage: (
    uuid: string,
    client_uuid: string | null,
    patient_uuid: string | null,
  ) =>
    requestVoid(() =>
      apiClient.PUT("/email/messages/{message_uuid}/association", {
        params: { path: { message_uuid: uuid } },
        body: { client_uuid, patient_uuid },
      }),
    ),
  getMessage: (uuid: string) =>
    request(() =>
      apiClient.GET("/email/messages/{message_uuid}", {
        params: { path: { message_uuid: uuid } },
      }),
    ).then(toMessage),
  getDraft: (uuid: string) =>
    request(() =>
      apiClient.GET("/email/drafts/{draft_uuid}", {
        params: { path: { draft_uuid: uuid } },
      }),
    ).then(toDraft),
  discardDraft: (uuid: string) =>
    requestVoid(() =>
      apiClient.DELETE("/email/drafts/{draft_uuid}", {
        params: { path: { draft_uuid: uuid } },
      }),
    ),
  reviseDraft: (uuid: string, body: EmailDraftRevision) =>
    request(() =>
      apiClient.PUT("/email/drafts/{draft_uuid}", {
        params: { path: { draft_uuid: uuid } },
        body,
      }),
    ).then(toDraft),
  approveDraft: (uuid: string, revision: number) =>
    requestVoid(() =>
      apiClient.POST("/email/drafts/{draft_uuid}/approve", {
        params: { path: { draft_uuid: uuid } },
        body: { revision },
      }),
    ),
  getAssistant: () => request(() => apiClient.GET("/email/assistant")),
  saveAssistant: (body: EmailAssistantIn) =>
    request(() => apiClient.PUT("/email/assistant", { body })),
};
